import React from 'react'
import config from '#src/config'
import { Experiment, Variation } from '@deal/experiment-js'
import { FancyModal, Button } from '@deal/components'
import { IdentityConsumer } from '#src/containers/Identity'
import ShortLinkModal from '#src/components/Share/ShortLinkModal'
import EmailCollectionModal from '#src/components/Share/EmailCollectionModal'
import FacebookShare from '../Share/FacebookShare'
import Comment from './comment.svg'
import Gift from './gift.svg'
import Email from './share-email.svg'
import Facebook from './share-facebook.svg'
import Link from './share-link.svg'
import styles from './styles.css'

interface Props {
  expertId: string
  vanityId?: string | null
  expertFirstName?: string | null
  itemsSent?: number
  messagesCount?: number
  noStats?: boolean
  handleClose: () => void
}

interface ComponentProps extends Props {
  headline: string
  subheadline: string
}

interface State {
  sharedModalOpened?: string
}

class ReferralModalComponent extends React.Component<ComponentProps, State> {
  constructor(props: ComponentProps) {
    super(props)
    this.state = { sharedModalOpened: '' }
  }

  public generateShareUrl(expertId: string, vanityId?: string | null, userId?: string) {
    const sharePath = `/experts/${vanityId ? vanityId : expertId}${
      userId ? `?referrer_id=${userId}` : ''
    }`
    return `${config.get('openGraph.url')}${sharePath}`
  }

  public handleOpenShareModal = (type: string) => {
    this.setState({ sharedModalOpened: type })
  }

  public handleCloseShareModal = () => this.setState({ sharedModalOpened: '' })

  render() {
    const {
      expertId,
      vanityId,
      expertFirstName,
      itemsSent,
      messagesCount,
      noStats,
      headline,
      subheadline,
      handleClose
    } = this.props
    const { sharedModalOpened } = this.state
    return (
      <IdentityConsumer>
        {({ myself }) => {
          const shareUrl = this.generateShareUrl(
            expertId,
            vanityId,
            myself ? myself.user.id : undefined
          )

          if (sharedModalOpened === 'email') {
            return (
              <EmailCollectionModal
                onRequestClose={this.handleCloseShareModal}
                expertId={expertId}
              />
            )
          }

          if (sharedModalOpened === 'share') {
            return (
              <ShortLinkModal
                shareUrl={shareUrl}
                preferredDomain="curatedrefer.com"
                onRequestClose={this.handleCloseShareModal}
              />
            )
          }

          return (
            <FancyModal isOpen onRequestClose={handleClose} className={styles.modal}>
              <div className={styles.container}>
                <div className={styles.header}>{headline}</div>
                <div className={styles.subheader}>{subheadline}</div>
                <div className={styles.linksContainer}>
                  <div className={styles.linksTitle}>Share your link</div>
                  <div className={styles.buttonsContainer}>
                    <FacebookShare shareUrl={shareUrl}>
                      {share => (
                        <Button className={styles.facebookButton} onClick={() => share()}>
                          <Facebook /> Facebook
                        </Button>
                      )}
                    </FacebookShare>
                    <Button
                      className={styles.emailButton}
                      onClick={() => this.handleOpenShareModal('email')}
                    >
                      <Email /> Email
                    </Button>
                    <Button
                      className={styles.copyButton}
                      onClick={() => this.handleOpenShareModal('share')}
                    >
                      <Link /> Copy Link
                    </Button>
                  </div>
                </div>
                {!noStats && (
                  <div className={styles.header}>
                    Your experience with {expertFirstName}
                    <div className={styles.statsContainer}>
                      {messagesCount && (
                        <div className={styles.stat}>
                          <div className={styles.statInner}>
                            <Comment />
                            <div className={styles.statContent}>
                              <div className={styles.statNumber}>{messagesCount}</div>
                              <div className={styles.statTextBig}>Messages</div>
                            </div>
                          </div>
                          <div className={styles.statTextSmall}>Messages</div>
                        </div>
                      )}
                      {itemsSent && (
                        <div className={styles.stat}>
                          <div className={styles.statInner}>
                            <Gift />
                            <div className={styles.statContent}>
                              <div className={styles.statNumber}>{itemsSent}</div>
                              <div className={styles.statTextBig}>Items sent</div>
                            </div>
                          </div>
                          <div className={styles.statTextSmall}>Items sent</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </FancyModal>
          )
        }}
      </IdentityConsumer>
    )
  }
}

const ReferralModal: React.SFC<Props> = props => (
  <Experiment id="referral-modal" default="v1">
    <Variation id="v1">
      <ReferralModalComponent
        {...props}
        headline={`Share ${props.expertFirstName}’s profile`}
        subheadline={`Invite your friends to shop with ${
          props.expertFirstName
        }! Get $25 for every friend that purchases and they get $25 too!`}
      />
    </Variation>
    <Variation id="v1-discount">
      <ReferralModalComponent
        {...props}
        headline="Give your friends $25 off"
        subheadline={`Invite your friends to shop with ${
          props.expertFirstName
        }! Get $25 for every friend that purchases and they get $25 too!`}
      />
    </Variation>
    <Variation id="v2">
      <ReferralModalComponent
        {...props}
        headline="Hook your friends up!"
        subheadline={`Invite your friends to shop with ${
          props.expertFirstName
        }! They'll each get $25 off their first purchase.`}
      />
    </Variation>
    <Variation id="v3">
      <ReferralModalComponent
        {...props}
        headline={`Give your friends $25 to shop with ${props.expertFirstName}!`}
        subheadline={`Introduce your friends to ${
          props.expertFirstName
        } - they'll each get $25 off their first purchase!`}
      />
    </Variation>
    <Variation id="v4">
      <ReferralModalComponent
        {...props}
        headline={`Give your friends $25!`}
        subheadline={`You’re a valued Curated customer. That’s why we’re giving you $25 to gift each friend who shops with ${
          props.expertFirstName
        }. So go ahead - share the love!`}
      />
    </Variation>
  </Experiment>
)

export default ReferralModal
